import {
    ButtonLink,
    ButtonPrimary,
    Callout,
    IconInformationUserRegular,
    PhoneNumberField,
    skinVars,
    Text2,
    useDialog,
} from '@telefonica/mistica';
import { AxiosError, AxiosResponse } from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    createNewContact,
    createNewProblem,
    getListContactsProblems,
    getProblemById,
} from '../../services/ProblemService';
import ChatSupport from '../ChatSupport';
import ChatText from '../ChatText';
import './index.scss';
import { formatMobileNumber } from '../../utils/dataTransformation/unitsConversion';
import useGlobalRequest, { EGlobalRequest } from '../../hooks/useGlobalRequest';
import {
    IProblemContact,
    IProblemDiagnosis,
    EProblemFeedback,
    EProblemFeedbackPhase,
    EProblemFeedbackType,
    IProblemIssue,
    IIndexGeneric,
    EProblemErrorMessage,
} from '../../models/Problems.interfaces';
import ChatSupportChecks from '../ChatSupportChecks';
import { handleValidationRegExp, setInfoError } from '../../utils/dataTransformation/errorMesssages';
import navigateParams from '../../utils/navigation/navigateParams';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { literalsFeedback, LoaderMessage } from '../../pages/support/utils.chat';
import { EValidations } from '../../models/interfaces';
import useUserlight from '../../hooks/useUserLight';

interface IChatIncidentContactNumber {
    problem: IProblemDiagnosis;
    onEmitContactNumber: Function;
}

export const ChatIncidenteContactNumber: React.FC<IChatIncidentContactNumber> = ({
    problem,
    onEmitContactNumber,
}: IChatIncidentContactNumber) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const isLight = useUserlight();
    const [loading, setLoading] = useState<boolean>(false);
    const [selectContactNumber, setSelectContactNumber] = useState(true);
    const [selectedContactNumber, setSelectedContactNumber] = useState<string>('');
    const [selectedAddContactNumber, setSelectedAddContactNumber] = useState(true);
    const [number, setNumber] = useState('');
    const [contactWrittenId, setContactWrittenId] = useState('');
    const [user] = useGlobalRequest(EGlobalRequest.USER);
    const [lottieLoading, setLottieLoading] = useState<any>(true);
    const [contacts, setContacts] = useState<IProblemContact[]>([]);
    const [error, setError] = useState<boolean>(false);
    const { dialog } = useDialog();

    const selectTabMotive = (number: number | string) => {
        postCreateProblems(`${number}`);
        setSelectContactNumber(false);
        setSelectedContactNumber(number as any);
    };

    const selectedMotiveProblem = () => {
        if (selectContactNumber === false)
            return <ChatText context={'response'} text={formatMobileNumber(selectedContactNumber)}></ChatText>;
    };

    useEffect(() => {
        getListContactsProblems()
            .then((response: any) => {
                const { data } = response;
                const { contact } = data;
                const email = (contact as IProblemContact[]).filter((item: IProblemContact) => item.type === 'EMAI')[0]
                    .id;
                const phones = (contact as IProblemContact[]).filter((item: IProblemContact) => item.type === 'TELF');
                setContacts(phones);
                setContactWrittenId(email);
                setLottieLoading(false);
                window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
            })
            .catch((error: AxiosError) => {
                console.error(error);
                const queryParams = {
                    phase: EProblemFeedbackType.ERROR,
                    error: EProblemFeedback.DIAGNOSIS,
                    ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
                };
                if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                    navigate(`/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`);
                }
            });
    }, []);

    const addNumberContact = () => {
        setSelectContactNumber(false);
        setSelectedAddContactNumber(false);
        window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
    };

    const handleInputChange = (event: any) => {
        setNumber(event);
    };

    const postCreateProblems = (contactCallId: string) => {
        setLoading(true);
        createNewProblem({
            productId: problem.products[0].productPsId,
            customerId: user.id,
            motiveId: problem.motive.id,
            contactWrittenId,
            contactCallId,
        })
            .then((response: AxiosResponse) => {
                const { data }: { data: IIndexGeneric } = response;
                getProblemById({ problemId: data.id })
                    .then((response: AxiosResponse) => {
                        const { code }: IProblemIssue = response.data;
                        setLoading(false);
                        setParams({ code });
                        onEmitContactNumber({ product: { problemId: data.id }, step: 6 });
                    })
                    .catch((error: AxiosError) => {
                        console.error(error);

                        const queryParams = {
                            phase: EProblemFeedbackType.ERROR,
                            error: EProblemFeedback.DIAGNOSIS,
                            ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
                        };
                        if (navigateParams('feedback-info', '/support/incidents-feedback', queryParams)) {
                            navigate(
                                `/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`,
                            );
                        }
                    });
            })
            .catch((error: AxiosError) => {
                setLoading(false);
                const { localizedDescription } = setInfoError(error);
                const queryParams = {
                    error_exist: {
                        type: EProblemFeedbackType.INFO,
                        phase: EProblemFeedbackPhase.EXT,
                        ...literalsFeedback(EProblemFeedback.EXIST_ISSUE),
                    },
                    error_diagnosis: {
                        phase: EProblemFeedbackType.ERROR,
                        error: EProblemFeedback.DIAGNOSIS,
                        ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
                    },
                    error_masive: {
                        phase: EProblemFeedbackType.ERROR,
                        error: EProblemFeedback.ERROR_MASIVE,
                        ...literalsFeedback(EProblemFeedback.ERROR_MASIVE),
                    },
                    error_connection: {
                        phase: EProblemFeedbackType.ERROR,
                        error: EProblemFeedback.ERROR_CONNECTION,
                        ...literalsFeedback(EProblemFeedback.ERROR_CONNECTION),
                    },
                };

                if (localizedDescription === EProblemErrorMessage.ERROR_EXIST_ISSUE) {
                    if (navigateParams('feedback-info', '/support/incidents-feedback', queryParams.error_exist)) {
                        navigate(
                            `/support/incidents-feedback?type=${queryParams.error_exist.type}&phase=${queryParams.error_exist.phase}`,
                        );
                    }
                } else if (localizedDescription === EProblemErrorMessage.ERROR_MASIVE) {
                    if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams.error_masive)) {
                        navigate(
                            `/support/incidents-feedback?phase=${queryParams.error_masive.phase}&error=${queryParams.error_masive.error}`,
                        );
                    }
                } else if (localizedDescription === EProblemErrorMessage.ERROR_CONNECTION) {
                    if (navigateParams('feedback-info', '/support/incidents-feedback', queryParams.error_exist)) {
                        navigate(
                            `/support/incidents-feedback?phase=${queryParams.error_connection.phase}&error=${queryParams.error_connection.error}`,
                        );
                    }
                } else {
                    if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams.error_diagnosis)) {
                        navigate(
                            `/support/incidents-feedback?phase=${queryParams.error_diagnosis.phase}&error=${queryParams.error_diagnosis.error}`,
                        );
                    }
                }
            });
    };

    const sendContactNumber = (number: number) => {
        const error = handleValidationRegExp(String(number), EValidations.phone);
        if (!error) {
            setError(!error);
        } else {
            let message = (Math.floor(Math.random() * 99999999999999) + 1).toString();
            let message2 = (Math.floor(Math.random() * 99999999999999) + 1).toString();
            const existNumber = contacts.some(({ value }: IProblemContact) => value === String(number).trim());
            const cId = message + message2;

            if (!existNumber) {
                setLoading(true);
                createNewContact({
                    id: cId,
                    type: 'TELF',
                    value: number,
                    favourite: false,
                })
                    .then((response: AxiosResponse) => {
                        const { data } = response;
                        const { id } = data;
                        postCreateProblems(`${id}`);
                        setSelectContactNumber(false);
                        setSelectedAddContactNumber(true);
                        setSelectedContactNumber(number as any);
                        window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
                    })
                    .catch((error: AxiosError) => {
                        console.error(error);
                        setLoading(false);
                        const queryParams = {
                            phase: EProblemFeedbackType.ERROR,
                            error: EProblemFeedback.DIAGNOSIS,
                            ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
                        };
                        if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                            navigate(
                                `/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`,
                            );
                        }
                    });
            } else {
                dialog({
                    title: t('supportChatSameContactTitle'),
                    message: t('supportChatSameContact'),
                    asset: (
                        <IconInformationUserRegular
                            size={64}
                            color={skinVars.colors.brand}
                        ></IconInformationUserRegular>
                    ),
                    acceptText: t('buttonUnderstoodFeedback'),
                    onAccept() {
                        setSelectedAddContactNumber(true);
                        setSelectContactNumber(true);
                    },
                });
            }
        }
    };

    const setAddNumberContact = () => {
        return (
            <div className='chatIncidenteContactNumber__input'>
                <PhoneNumberField
                    name={'phone'}
                    error={error}
                    label={t('personalDataMobile')}
                    helperText={
                        error
                            ? `${t('supportExampleChatNewContactPhoneError')}`
                            : t('supportExampleChatNewContactPhone')
                    }
                    onChangeValue={handleInputChange}
                ></PhoneNumberField>
                <ButtonPrimary
                    small
                    className='chatIncidenteContactNumber__input-button'
                    disabled={number?.length != 9}
                    onPress={() => {
                        sendContactNumber(number as any);
                    }}
                >
                    {t('continue')}
                </ButtonPrimary>
            </div>
        );
    };

    return (
        <div className='chatIncidenteContactNumber'>
            {lottieLoading ? (
                LoaderMessage
            ) : (
                <div>
                    <ChatSupport text={t('supportChatIncidentContactNumberAnswer')}></ChatSupport>
                    {selectContactNumber && (
                        <>
                            {contacts.map((contact: IProblemContact, key: number) => {
                                const { id, value } = contact;
                                return (
                                    <div key={key}>
                                        <div
                                            onClick={() => {
                                                selectTabMotive(id);
                                                setSelectedContactNumber(`${value}`);
                                            }}
                                            className='chatIncidenteContactNumber__number'
                                            style={{
                                                borderStyle: 'solid',
                                                borderWidth: '1px',
                                                borderColor: skinVars.colors.controlActivated,
                                            }}
                                        >
                                            <Text2 regular color={skinVars.colors.textLink}>
                                                {formatMobileNumber(value)}
                                            </Text2>
                                        </div>
                                    </div>
                                );
                            })}
                            {isLight && (
                                <div className='chatIncidenteContactNumber__callout'>
                                    <Callout
                                        asset={<IconInformationUserRegular size={24} color={skinVars.colors.brand} />}
                                        description={t('autodiagnosisDescriptionCalloutContacNumber')}
                                        buttonLink={
                                            <ButtonLink
                                                onPress={() => {
                                                    if (navigateParams('navigation-native-logout', '', {}, '', '')) {
                                                        navigate('/logout');
                                                    }
                                                }}
                                            >
                                                {t('login')}
                                            </ButtonLink>
                                        }
                                    ></Callout>
                                </div>
                            )}
                            {!isLight && (
                                <div
                                    onClick={() => {
                                        addNumberContact();
                                    }}
                                    className='chatIncidenteContactNumber__number'
                                    style={{
                                        borderStyle: 'solid',
                                        borderWidth: '1px',
                                        borderColor: skinVars.colors.controlActivated,
                                    }}
                                >
                                    <Text2 regular color={skinVars.colors.textLink}>
                                        {t('supportChatChecksWritteNumber')}
                                    </Text2>
                                </div>
                            )}
                        </>
                    )}
                    {!selectContactNumber && selectedAddContactNumber && selectedMotiveProblem()}
                    {!selectedAddContactNumber && setAddNumberContact()}
                    {loading && <ChatSupportChecks></ChatSupportChecks>}
                </div>
            )}
        </div>
    );
};

export default ChatIncidenteContactNumber;
